// src/components/Event.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import './Event.css';

const formatDate = (s) => {
  if (!s) return '未設定';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

const hhmm = (iso) => {
  if (!iso) return '';
  const t = iso.includes('T') ? iso.split('T')[1] : iso;
  return t.substring(0, 5);
};

const parseArray = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try { return JSON.parse(val); } catch { return [val]; }
};

export default function Event() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [isParticipating, setIsParticipating] = useState(null);
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!eventId) { setErr('イベントIDが不正です。'); setLoading(false); return; }
    api.get(`/events/${eventId}/`)
      .then((res) => {
        const data = res.data;
        setEvent(data);
        // 既回答がある場合は初期値にセット
        if (data.userStatus === 'yes') { setIsParticipating(true); }
        if (data.userStatus === 'no')  { setIsParticipating(false); setReason(data.reason || ''); }
      })
      .catch((e) => { setErr('イベント取得エラーが発生しました。'); console.error(e); })
      .finally(() => setLoading(false));
  }, [eventId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isParticipating === null) { alert('参加・不参加を選択してください。'); return; }
    if (isParticipating === false && !reason.trim()) {
      alert('参加しない場合は理由を入力してください。'); return;
    }
    try {
      await api.post(`/events/${eventId}/participate/`, { isParticipating, reason });
      setSubmitted(true);
      alert('回答を送信しました。');
      navigate('/events');
    } catch (e) {
      console.error(e);
      alert('送信時にエラーが発生しました。');
    }
  };

  if (loading) return <div className="text-center mt-5">読み込み中...</div>;
  if (err)     return <div className="text-danger mt-5 text-center">{err}</div>;
  if (!event)  return <div className="text-center mt-5">イベントが見つかりません。</div>;

  const rawTargets = parseArray(event.targetGroup);
  // dept:/sec: を集約、単独の「部署単位」は除去
  const deptNames  = rawTargets.filter(t => typeof t === 'string' && t.startsWith('dept:')).map(t => t.slice(5));
  const secNames   = rawTargets.filter(t => typeof t === 'string' && t.startsWith('sec:')).map(t => t.slice(4));
  const otherTargets = rawTargets.filter(t => typeof t === 'string' && !t.startsWith('dept:') && !t.startsWith('sec:') && t !== '部署単位');
  const targets = [
    ...otherTargets,
    ...(deptNames.length > 0 ? [`部署単位：${deptNames.join('、')}`] : []),
    ...(secNames.length  > 0 ? [`課単位：${secNames.join('、')}`]   : []),
  ];

  const policies  = parseArray(event.rainPolicy);
  const generals  = parseArray(event.policy);

  // オンライン判定
  const ONLINE_PREFIXES = ['Zoom: ', 'Teams: ', 'Google Meet: ', 'Skype: ', 'Slack: '];
  const detectedPrefix = ONLINE_PREFIXES.find(p => (event.location || '').startsWith(p));
  const isOnline  = Boolean(detectedPrefix);
  const isZoom    = event.location?.toLowerCase().includes('zoom');
  const onlineUrl = detectedPrefix ? event.location.replace(detectedPrefix, '') : (event.location || '');
  // 回答締切は「当日いっぱい」回答可能とする（締切日の翌日0時で締切）。
  const deadlinePassed = (() => {
    if (!event.regDeadline) return false;
    const d = new Date(event.regDeadline);
    const endOfDeadlineDay = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    return endOfDeadlineDay <= new Date();
  })();

  return (
    <div className="event-detail-page">
      <div className="event-detail-card">

        {/* ヘッダー */}
        <div className="event-detail-header">
          <h2 className="event-detail-title">{event.eventName}</h2>
          {event.is_cancelled ? (
            <span className="current-status status-cancelled">🚫 中止</span>
          ) : event.userStatus === 'yes' ? (
            <span className="current-status status-yes">✅ 参加予定</span>
          ) : event.userStatus === 'no' ? (
            <span className="current-status status-no">❌ 不参加</span>
          ) : (
            <span className="current-status status-pending">⏳ 未回答</span>
          )}
        </div>

        {event.is_cancelled ? (
          <div className="event-cancelled-banner event-cancelled-banner-lg">
            <p className="event-cancelled-banner-badge">🚫 中止</p>
            <h4>このイベントは中止されました</h4>
            <div className="event-cancelled-reason-box">
              <span className="event-cancelled-reason-label">中止理由</span>
              <p className="event-cancelled-reason">
                {event.cancel_reason || '（理由は登録されていません）'}
              </p>
            </div>
          </div>
        ) : (
        <>
        {/* イベント情報 */}
        <div className="event-info-grid">
          <div className="info-row">
            <span className="info-label">📅 開催日</span>
            <span className="info-value">
              {formatDate(event.eventDate)}
              {event.eventEndDate && event.eventEndDate !== event.eventDate
                ? ` 〜 ${formatDate(event.eventEndDate)}`
                : ''}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">🕐 時間</span>
            <span className="info-value">{hhmm(event.startTime)} 〜 {hhmm(event.endTime)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">📍 場所</span>
            <span className="info-value">
              {isOnline
                ? <a href={onlineUrl} target="_blank" rel="noreferrer">{onlineUrl}</a>
                : (event.location || '未設定')}
            </span>
          </div>
          {isOnline && event.meeting_id && (
            <div className="info-row">
              <span className="info-label">📋 Meeting ID</span>
              <span className="info-value" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{event.meeting_id}</span>
            </div>
          )}
          {isOnline && event.passcode && (
            <div className="info-row">
              <span className="info-label">🔑 パスコード</span>
              <span className="info-value" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{event.passcode}</span>
            </div>
          )}
          {event.weather && (
            <div className="info-row">
              <span className="info-label">☁️ 天気</span>
              <span className="info-value">{event.weather}</span>
            </div>
          )}
          {policies.length > 0 && (
            <div className="info-row">
              <span className="info-label">🌧 雨天時</span>
              <span className="info-value">{policies.join(' / ')}</span>
            </div>
          )}
          {generals.length > 0 && (
            <div className="info-row">
              <span className="info-label">📋 規定</span>
              <span className="info-value">{generals.join(' / ')}</span>
            </div>
          )}
          {targets.length > 0 && (
            <div className="info-row">
              <span className="info-label">👥 対象者</span>
              <span className="info-value">{targets.join('、')}</span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">⏰ 回答締切</span>
            <span className={`info-value ${deadlinePassed ? 'deadline-passed' : ''}`}>
              {formatDate(event.regDeadline)}
              {deadlinePassed && ' （締切済み）'}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">📝 アンケート</span>
            <span className="info-value">{event.hasAnketo ? 'あり' : 'なし'}</span>
          </div>
        </div>

        {/* イベント説明 */}
        {event.description && (
          <div className="event-description-block">
            <h4>イベント内容</h4>
            <p>{event.description}</p>
          </div>
        )}

        {/* アンケートリンク */}
        {event.hasAnketo && event.userStatus === 'yes' && !submitted && (
          <div className="anketo-notice">
            <a href={`/event/${event.id}/anketo`} className="anketo-link">
              📋 アンケートに回答する（参加者必須）
            </a>
          </div>
        )}

        <hr />
        </>
        )}

        {/* 参加回答フォーム */}
        {event.is_cancelled ? (
          <div className="button-group event-detail-buttons">
            <button type="button" className="btn-back" onClick={() => navigate('/events')}>
              戻る
            </button>
          </div>
        ) : deadlinePassed ? (
          <p className="deadline-notice">回答締切が過ぎています。</p>
        ) : (
          <form onSubmit={handleSubmit} className="participation-form">
            <h4>参加確認</h4>
            <div className="radio-group-inline">
              <label className="radio-option">
                <input
                  type="radio"
                  name="participation"
                  checked={isParticipating === true}
                  onChange={() => setIsParticipating(true)}
                />
                <span>参加する</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="participation"
                  checked={isParticipating === false}
                  onChange={() => setIsParticipating(false)}
                />
                <span>参加しない</span>
              </label>
            </div>

            {isParticipating === false && (
              <div className="reason-block">
                <label className="reason-label">不参加の理由</label>
                <textarea
                  className="reason-textarea"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="理由を入力してください"
                  rows={3}
                />
              </div>
            )}

            <div className="button-group event-detail-buttons">
              <button type="button" className="btn-back" onClick={() => navigate('/events')}>
                戻る
              </button>
              <button type="submit" className="btn-send">送信</button>
            </div>
          </form>
        )}

        {deadlinePassed && (
          <div className="button-group event-detail-buttons">
            <button type="button" className="btn-back" onClick={() => navigate('/events')}>
              戻る
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
